import { useEffect, useState } from "react";
import { getInlineLoader } from "../Helpers";
import overlay from "../assets/banner6.jpg";
import {
  FaArrowTrendUp,
  FaDisplay,
  FaHandshake,
  FaPeopleGroup,
  FaServer,
  FaShieldHalved,
  FaSitemap,
} from "react-icons/fa6";

export default function Staff() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setReady(false);
    const timer = setTimeout(() => setReady(true), 300);
    return () => clearTimeout(timer);
  }, []);

  if (!ready) {
    return (
      <div style={{ display: 'flex', height: '100vh', width: '100%', alignItems: 'center', justifyContent: 'center', backgroundColor: '#ffffff' }}>
        {getInlineLoader()}
      </div>
    );
  }

  const strengths = [
    {
      icon: <FaPeopleGroup size={24} />,
      text: "Client-centered collaboration shaped around real operational needs.",
    },
    {
      icon: <FaShieldHalved size={24} />,
      text: "A dependable approach to reliability, continuity, and technology stewardship.",
    },
    {
      icon: <FaArrowTrendUp size={24} />,
      text: "Future-ready solutions designed to scale with growth and change.",
    },
    {
      icon: <FaHandshake size={24} />,
      text: "Partnerships built on integrity, responsiveness, and consistent delivery.",
    },
  ];

  const capabilityCards = [
    {
      icon: <FaDisplay size={22} />,
      title: "Digital Experience",
      copy:
        "Purposeful interfaces and applications that make day-to-day work clearer and more efficient.",
    },
    {
      icon: <FaServer size={22} />,
      title: "Systems Reliability",
      copy:
        "Structured infrastructure and support models that improve uptime and operational confidence.",
    },
    {
      icon: <FaSitemap size={22} />,
      title: "Integrated Delivery",
      copy:
        "Joined-up thinking across consulting, implementation, support, and continuous improvement.",
    },
  ];

  return (
    <>
      <style>{`
        :root {
          --primaryColor: #FFC511;
          --onPrimaryColor: #fefefe;
          --secondaryColor: #0089D8;
          --onSecondaryColor: #fefefe;
          --surfaceColor: #ffffff;
          --onSurfaceColor: #0c2d41;
          --backgroundColor: #ffffff;
          --onBackgroundColor: #0b2635;
          --extraColor: #e4c970;
          --font-main: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        }

        .tech-staff-section {
          font-family: var(--font-main);
          background-color: var(--backgroundColor);
          color: var(--onBackgroundColor);
          position: relative;
          padding: 6rem 1.5rem;
          overflow: hidden;
        }
        @media (min-width: 768px) {
          .tech-staff-section {
            padding: 8rem 2rem;
          }
        }

        /* Background */
        .tech-bg-image {
          position: absolute;
          inset: 0;
          background-image: url(${overlay});
          background-size: cover;
          background-position: center;
          opacity: 0.06;
          z-index: 0;
          filter: grayscale(100%);
        }
        .tech-bg-accent {
          position: absolute;
          top: -20%;
          right: -10%;
          width: 600px;
          height: 600px;
          background: radial-gradient(circle, rgba(0, 137, 216, 0.08) 0%, transparent 70%);
          border-radius: 50%;
          z-index: 0;
          pointer-events: none;
        }

        .tech-container {
          position: relative;
          z-index: 1;
          max-width: 1200px;
          margin: 0 auto;
        }

        /* Header */
        .tech-header {
          text-align: center;
          max-width: 700px;
          margin: 0 auto 5rem auto;
        }
        .tech-kicker {
          display: block;
          font-size: 0.8rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--secondaryColor);
          margin-bottom: 1.25rem;
        }
        .tech-title {
          font-size: 2.5rem;
          font-weight: 700;
          letter-spacing: -0.025em;
          margin: 0 0 1.5rem 0;
          color: var(--onBackgroundColor);
          line-height: 1.15;
        }
        @media (min-width: 768px) {
          .tech-title {
            font-size: 3.5rem;
          }
        }
        .tech-description {
          font-size: 1.125rem;
          line-height: 1.7;
          color: #4b5563;
          margin: 0;
          font-weight: 400;
        }

        /* Strengths List - Minimal, no cards */
        .tech-strengths-list {
          display: grid;
          grid-template-columns: 1fr;
          gap: 0;
          margin-bottom: 5rem;
          border-top: 1px solid #e5e7eb;
        }
        @media (min-width: 640px) {
          .tech-strengths-list {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        @media (min-width: 1024px) {
          .tech-strengths-list {
            grid-template-columns: repeat(4, 1fr);
          }
        }
        .tech-strength-item {
          display: flex;
          flex-direction: column;
          padding: 2rem 0;
          border-bottom: 1px solid #e5e7eb;
          transition: background-color 0.3s ease;
        }
        @media (min-width: 1024px) {
          .tech-strength-item {
            border-bottom: none;
            border-right: 1px solid #e5e7eb;
            padding: 0 2rem;
          }
          .tech-strength-item:last-child {
            border-right: none;
          }
        }
        .tech-strength-item:hover {
          background-color: #f9fafb;
        }
        .tech-strength-icon {
          color: var(--secondaryColor);
          margin-bottom: 1.25rem;
          display: inline-flex;
          padding: 10px;
          background: rgba(0, 137, 216, 0.08);
          border-radius: 10px;
          width: fit-content;
        }
        .tech-strength-text {
          font-size: 1rem;
          line-height: 1.6;
          color: #374151;
          margin: 0;
          font-weight: 400;
        }

        /* Divider */
        .tech-divider {
          height: 1px;
          background: linear-gradient(to right, #e5e7eb, transparent);
          margin: 4rem 0;
        }

        /* Capabilities - Clean list style */
        .tech-capabilities-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 3rem;
        }
        @media (min-width: 768px) {
          .tech-capabilities-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 4rem;
          }
        }
        .tech-capability-item {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          padding: 0;
        }
        .tech-capability-icon {
          color: var(--primaryColor);
          font-size: 1.75rem;
          margin-bottom: 1.5rem;
          display: inline-flex;
        }
        .tech-capability-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--onBackgroundColor);
          margin: 0 0 0.75rem 0;
          letter-spacing: -0.01em;
        }
        .tech-capability-copy {
          font-size: 0.95rem;
          color: #6b7280;
          line-height: 1.6;
          margin: 0;
        }

        /* Hover accent line */
        .tech-capability-item::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 0;
          height: 2px;
          background: var(--secondaryColor);
          transition: width 0.3s ease;
        }
        .tech-capability-item {
          position: relative;
        }
        .tech-capability-item:hover::after {
          width: 40px;
        }
      `}</style>

      <section className="tech-staff-section">
        <div className="tech-bg-image"></div>
        <div className="tech-bg-accent"></div>
        
        <div className="tech-container">
          {/* Header */}
          <div className="tech-header" data-aos="fade-up">
            <span className="tech-kicker">Why Choose Nova</span>
            <h2 className="tech-title">
              A trusted partner for modern ICT transformation
            </h2>
            <p className="tech-description">
              Nova Technologies combines industry insight, service quality, and a practical
              delivery mindset to support organizations seeking scalable, high-performing
              digital solutions.
            </p>
          </div>

          {/* Strengths - Minimal list, no cards */}
          <div className="tech-strengths-list">
            {strengths.map((item, idx) => (
              <div
                key={item.text}
                className="tech-strength-item p-3 bg-primary bg-opacity-10"
                data-aos="fade-up"
                data-aos-delay={idx * 80}
              >
                <div className="tech-strength-icon bg-secondary text-light">
                  {item.icon}
                </div>
                <p className="tech-strength-text font-semi-bold">{item.text}</p>
              </div>
            ))}
          </div>

          <div className="tech-divider"></div>

          {/* Capabilities - Clean vertical list */}
          <div className="tech-capabilities-grid">
            {capabilityCards.map((card, idx) => (
              <div
                key={card.title}
                className="tech-capability-item"
                data-aos="fade-up"
                data-aos-delay={idx * 100}
              >
                <div className="tech-capability-icon">
                  {card.icon}
                </div>
                <h3 className="tech-capability-title">{card.title}</h3>
                <p className="tech-capability-copy">{card.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}